import { useThemedLogo } from "../../theme/useThemedLogo";
import logoAccenture from "../../assets/logo-accenture.png";
import logoLabournet from "../../assets/logo-labournet.png";
import logoScaleupHealth from "../../assets/logo-scaleuphealth.png";
import { useRedmonkLogo } from "../../theme/useRedmonkLogo";
import React from "react";
import {
  Typography,
  Button,
  Row,
  Col,
  Space,
  Flex,
  Layout,
} from "antd";
import PublicHeader from "../../components/PublicHeader";
import MarketingFooter from "../../components/MarketingFooter";
import Seo from "../../components/Seo";
import EmbeddedLeadForm from "../../components/EmbeddedLeadForm";
import CaptureFlowAnimation from "../../components/CaptureFlowAnimation";
import FormFillAnimation from "../../components/FormFillAnimation";
import QuickSendAnimation from "../../components/QuickSendAnimation";
import ImportWizardAnimation from "../../components/ImportWizardAnimation";
import SpamScanAnimation from "../../components/SpamScanAnimation";
import TemplatePickAnimation from "../../components/TemplatePickAnimation";
import DeliveryTrackingAnimation from "../../components/DeliveryTrackingAnimation";
import Reveal from "../../components/Reveal";
import {
  UsergroupAddOutlined,
  SignatureOutlined,
  FormOutlined,
  SafetyOutlined,
  FileTextOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  BulbOutlined,
  GlobalOutlined,
} from "@ant-design/icons";

const LANDING_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Mailinflow",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Email marketing and lead-gen forms platform for small teams — manage contacts, build campaigns, and capture leads with built-in anti-spam protection.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "INR",
  },
};

const { Title, Text, Paragraph } = Typography;

const LandingPage = () => {
  const logo = useThemedLogo();
  const redmonklogo = useRedmonkLogo();
  const features = [
    {
      icon: <FormOutlined />,
      title: "Embeddable Forms",
      description:
        "Capture leads from any website with a simple form action URL — no separate form builder needed. Review submissions in your inbox, then add the ones you trust as contacts, keeping unscreened traffic off your list and protecting your sender reputation from day one.",
      demo: <FormFillAnimation />,
    },
    {
      icon: <SignatureOutlined />,
      title: "Quick Sending",
      description:
        "Send your emails with minimal steps — compose the message, pick your list, and hit send. No drag-and-drop builder, no multi-step wizard, no template to configure first. If you can write an email, you can run a campaign.",
      demo: <QuickSendAnimation />,
    },
    {
      icon: <UsergroupAddOutlined />,
      title: "Contact Management",
      description:
        "Manage your contacts effortlessly — import an existing list, add people one at a time, or edit details as they change. Everything lives in one place, so you're never juggling spreadsheets alongside your sending tool.",
      demo: <ImportWizardAnimation />,
    },
    {
      icon: <SafetyOutlined />,
      title: "Spam Protection",
      description:
        "Every form submission is scored for spam before it ever becomes a contact — disposable emails, bot traffic, and spam keywords are caught automatically. Run it in shadow mode to just watch the scores, or enforce mode to block spam outright, protecting your sender reputation either way.",
      demo: <SpamScanAnimation />,
    },
    {
      icon: <FileTextOutlined />,
      title: "Public and Private Templates",
      description:
        "Save a template privately for just your team, or make it public so any organization on Mailinflow can start from it. Reuse your best-performing emails instead of writing from scratch every time you send.",
      demo: <TemplatePickAnimation />,
    },
    {
      icon: <EyeOutlined />,
      title: "Open and Click Tracking",
      description:
        "See which campaigns are actually landing — opens and link clicks are tracked automatically on every send, on by default for every account. No extra setup, no separate analytics tool to wire up, just a clear read on what your audience is actually engaging with.",
      demo: <DeliveryTrackingAnimation />,
    },
  ];
  const trustPillars = [
    {
      icon: <SafetyCertificateOutlined />,
      title: "Every submission is screened",
      description:
        "Disposable emails, spam keywords, and bot traffic are scored before a submission ever becomes a contact — protecting your sender reputation from day one.",
    },
    {
      icon: <BulbOutlined />,
      title: "Built by a design studio",
      description:
        "Mailinflow comes out of Redmonk Studios, a product design studio — the interface is built the way we'd want to use it ourselves, not bolted on.",
    },
    {
      icon: <GlobalOutlined />,
      title: "Priced for India, built for anywhere",
      description:
        "INR billing, no charge for contact storage, and a free tier that's actually usable — not a 14-day trial with a countdown.",
    },
  ];
  const accessToken = localStorage.getItem("accessToken");
  const newsletterFormId = process.env.REACT_APP_MARKETING_NEWSLETTER_FORM_ID;

  return (
      <Layout className="marketing-page">
        <Seo
          title="Effortless Email Marketing for Small Teams"
          description="Mailinflow is a pocket-friendly email marketing and lead-gen forms platform with built-in anti-spam protection for startups and small teams."
          path="/"
          jsonLd={LANDING_JSON_LD}
        />
        <div
          className="container"
          style={{ position: "fixed", left: 0, right: 0, zIndex: 999 }}
        >
          <PublicHeader logo={logo} accessToken={accessToken} />
        </div>

        <main>
          <div className="container">
            <section style={{ padding: "9rem 0 4rem" }}>
              <Row gutter={[48, 32]} align="middle">
                <Col xs={24} md={12}>
                  <span className="mk-eyebrow">Capture → Gate → Campaign</span>
                  <Title
                    style={{
                      fontSize: "clamp(34px, 4.5vw, 52px)",
                      margin: "1.25rem 0 1.5rem",
                      lineHeight: 1.12,
                    }}
                  >
                    Not every lead deserves a spot on your list.
                  </Title>
                  <Paragraph
                    style={{ fontSize: "17px", lineHeight: 1.65, maxWidth: 480 }}
                  >
                    Mailinflow captures form submissions, screens them for
                    spam before they ever become contacts, then sends the
                    campaign — one tool instead of the usual form builder,
                    Zapier, and email platform stack.
                  </Paragraph>
                  <Space size="large" style={{ marginTop: "0.5rem" }}>
                    <Button type="primary" size="large" href="/signup">
                      Get Started
                    </Button>
                    <Button
                      type="default"
                      size="large"
                      href="https://cal.com/d/uWZdkWEAbzT3L547YuKfua/book-a-demo-mailinflow"
                      target="_blank"
                    >
                      Book a Demo
                    </Button>
                  </Space>
                </Col>
                <Col xs={24} md={12}>
                  <CaptureFlowAnimation />
                </Col>
              </Row>
            </section>

            <section style={{ padding: "3rem 0", textAlign: "center" }}>
              <div className="mk-trustbar">
                <Flex gap={40} align="end" justify="center" wrap>
                  <img src={logoAccenture} alt="Accenture" />
                  <img src={logoLabournet} alt="LabourNet" />
                  <img src={logoScaleupHealth} alt="ScaleupHealth" />
                </Flex>
                <Paragraph className="mk-trustbar-caption" style={{ marginTop: "1.5rem", marginBottom: 0 }}>
                  Trusted by top-tier product companies
                </Paragraph>
              </div>
            </section>

            <Reveal
              as="section"
              className="mk-pillars-section"
              style={{ padding: "4rem 0" }}
            >
              <div className="mk-pillars-bg" aria-hidden="true">
                <SafetyCertificateOutlined className="mk-pillars-bg-icon mk-pillars-bg-icon-1" />
                <BulbOutlined className="mk-pillars-bg-icon mk-pillars-bg-icon-2" />
                <GlobalOutlined className="mk-pillars-bg-icon mk-pillars-bg-icon-3" />
                <SafetyCertificateOutlined className="mk-pillars-bg-icon mk-pillars-bg-icon-4" />
              </div>
              <div className="mk-pillars">
                {trustPillars.map((pillar) => (
                  <div className="mk-pillar" key={pillar.title}>
                    <span className="mk-pillar-mark">{pillar.icon}</span>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>{pillar.title}.</Text> {pillar.description}
                    </Paragraph>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal
              as="section"
              style={{ padding: "5rem 0 0", textAlign: "center" }}
            >
              <span className="mk-eyebrow">What's included</span>
              <Title level={2} style={{ margin: "1.25rem 0 0.75rem" }}>
                Services included in every plan
              </Title>
              <Paragraph
                type="secondary"
                style={{ maxWidth: 480, margin: "0 auto" }}
              >
                Every feature below ships in every plan — no upsells, no
                locked tiers.
              </Paragraph>
            </Reveal>

            {features.map((feature, index) => (
              <Reveal as="section" key={feature.title} className="mk-service-section">
                <div className="container">
                  <div
                    className={`mk-service-row${
                      index % 2 === 1 ? " mk-service-row--reverse" : ""
                    }`}
                  >
                    <div className="mk-service-stat">{feature.demo}</div>
                    <div className="mk-service-content">
                      <span className="mk-service-icon">{feature.icon}</span>
                      <Title level={3}>{feature.title}</Title>
                      <Paragraph>{feature.description}</Paragraph>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal
              as="section"
              style={{
                padding: "4rem 0",
                textAlign: "center",
              }}
            >
              <Space direction="vertical" align="center" style={{ display: "flex" }}>
                <Title
                  level={2}
                  style={{
                    marginBottom: "1rem",
                  }}
                >
                  Start capturing leads you can actually trust.
                </Title>
                <Paragraph style={{ maxWidth: 480 }}>
                  Every account includes 1 form, 100 submissions, and 100
                  emails free each month — the same terms for everyone,
                  right from the start.
                </Paragraph>
                <Button type="primary" size="large" href="/signup">
                  Get Started
                </Button>
              </Space>
            </Reveal>

            {newsletterFormId && (
              <Reveal as="section" style={{ padding: "2rem 0 4rem" }}>
                <Row justify="center">
                  <Col xs={24} sm={16} md={10}>
                    <Space
                      direction="vertical"
                      align="center"
                      style={{ width: "100%", textAlign: "center" }}
                    >
                      <Title level={3} style={{ marginBottom: "0.5rem" }}>
                        Get email marketing tips in your inbox
                      </Title>
                      <Paragraph type="secondary">
                        Occasional deliverability and lead-gen tips. No spam —
                        this form runs on our own anti-spam engine.
                      </Paragraph>
                    </Space>
                    <EmbeddedLeadForm
                      formId={newsletterFormId}
                      submitLabel="Subscribe"
                      successTitle="You're subscribed!"
                      successSubtitle="Watch your inbox for our next update."
                    />
                  </Col>
                </Row>
              </Reveal>
            )}
          </div>
        </main>

        <MarketingFooter logo={logo} redmonklogo={redmonklogo} />
      </Layout>
  );
};

export default LandingPage;
